import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header.js';
import { PAGE_CONTAINER_MARK, pageContainerStyle } from './layout.js';
import { LEGAL_ENTITY, LEGAL_LAST_UPDATED } from './legalDocs.js';

// Renders a legal document's intro + numbered sections (see the block
// format in legalDocs.js). Shared by LegalPage and the signup Terms of Use
// modal so both always show identical text.
export const LegalDocumentBody = ({ doc, headingStyle }) => (
  <>
    {doc.intro.map((text, i) => (
      <p key={`intro-${i}`}>{text}</p>
    ))}
    {doc.sections.map((section) => (
      <section key={section.title}>
        <h2 style={headingStyle}>{section.title}</h2>
        {section.blocks.map((block, i) => {
          if (typeof block === 'string') return <p key={i}>{block}</p>;
          if (block.list) {
            return (
              <ul key={i} style={{ paddingLeft: '22px' }}>
                {block.list.map((item) => (
                  <li key={item} style={{ marginBottom: '6px' }}>{item}</li>
                ))}
              </ul>
            );
          }
          return <p key={i}><strong>{block.text}</strong></p>;
        })}
      </section>
    ))}
  </>
);

// Footer links open each document in its own tab. "Return to previous page"
// closes that tab — handing the user back to the page they came from in the
// original tab. A tab opened by a link has a single history entry, which is
// what makes it script-closable; if the page was reached any other way
// (in-tab navigation, bookmark, shared link) we go back in history, or Home
// when there is nothing to go back to.
export const returnToPreviousPage = (navigate, win = window) => {
  if (win.history.length > 1) {
    navigate(-1);
    return;
  }
  win.close();
  // Browsers ignore close() for tabs they consider user-opened; fall back.
  setTimeout(() => {
    if (!win.closed) navigate('/');
  }, 150);
};

const css = `
  .legal-card {
    background-color: #FFFFFF;
    border-radius: 40px;
    padding: 40px 48px;
    box-sizing: border-box;
    color: #1F2D3D;
    font-family: 'Inter', sans-serif;
    font-size: 15px;
    line-height: 1.7;
    overflow-wrap: break-word;
  }
  .legal-card p { margin: 0 0 14px; }
  @media (max-width: 600px) {
    .legal-card {
      border-radius: 24px;
      padding: 24px 18px;
      font-size: 14px;
    }
  }
`;

const LegalPage = ({ doc, loggedInUser, setLoggedInUser }) => {
  const navigate = useNavigate();

  // Distinct tab titles so the three documents are easy to tell apart when
  // open side by side.
  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${doc.title} | Vicktoria AI™`;
    return () => {
      document.title = previousTitle;
    };
  }, [doc.title]);

  return (
    <div style={{ backgroundColor: '#D1DEE6', minHeight: '100vh' }}>
      <style>{css}</style>
      <Header
        loggedInUser={loggedInUser}
        setLoggedInUser={setLoggedInUser}
        topText=""
        midText="Vicktoria"
        botText={doc.title}
      />
      <main
        data-layout={PAGE_CONTAINER_MARK}
        style={{ ...pageContainerStyle, paddingTop: '20px', paddingBottom: '60px' }}
      >
        <div className="legal-card">
          <button
            type="button"
            onClick={() => returnToPreviousPage(navigate)}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              marginBottom: '24px',
              color: '#4A8EB3',
              fontFamily: "'Inter', sans-serif",
              fontSize: '15px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            ← Return to previous page
          </button>

          <div style={{ textAlign: 'center', marginBottom: '28px' }}>
            <h1 style={{ fontFamily: 'Judson, serif', fontSize: 'clamp(26px, 4vw, 34px)', margin: '0 0 6px', color: '#000000' }}>
              {doc.title.toUpperCase()}
            </h1>
            <p style={{ fontWeight: 'bold', margin: '0 0 4px' }}>{LEGAL_ENTITY}</p>
            <p style={{ fontStyle: 'italic', fontSize: '14px', color: '#2B4B6F', margin: 0 }}>
              Last Updated: {LEGAL_LAST_UPDATED}
            </p>
          </div>

          <LegalDocumentBody
            doc={doc}
            headingStyle={{ fontFamily: 'Judson, serif', fontSize: '20px', margin: '28px 0 10px', color: '#000000' }}
          />
        </div>
      </main>
    </div>
  );
};

export default LegalPage;
